import {
  TimeDisplayMode,
  formatTimeByMode,
} from "@/shared/components/visualization/WorkspaceCtx/TimeDisplayMode";
import { formatDateTimeNsWithOptions } from "@/shared/datetime/formatNanos";
import {
  DEFAULT_FRACTION_DIGITS,
  FractionDigits,
  NS_PER_MS_BIGINT,
  nanoSecToSecStr,
  nanoSecToUTCTimestamp,
} from "@/shared/time";

/**
 * How a wall-clock epoch-nanosecond timestamp should be rendered to text.
 *
 * Enum values are the stable identifiers used for persistence
 * (localStorage); display labels live in {@link TIMESTAMP_FORMAT_SPECS}.
 * Pure formatting — no React, no workspace access.
 *
 * The two context-dependent formats take their context through
 * {@link TimestampRenderOptions} rather than reading a live workspace, which is
 * what keeps this module free of Workspace access:
 * {@link TimestampFormat.Relative} counts from a caller-supplied anchor, and
 * {@link TimestampFormat.FollowVisualizer} renders through a caller-supplied
 * snapshot of the visualizer's own clock.
 */
export enum TimestampFormat {
  FollowVisualizer = "follow-visualizer",
  Nanoseconds = "ns",
  Microseconds = "us",
  Milliseconds = "ms",
  SecondsNanos = "seconds-nanos",
  Utc = "utc",
  LocalTime = "local",
  Relative = "relative",
}

/**
 * The visualizer's time rendering, as the chat needs to reproduce it: the mode
 * picked bottom-left in the workspace, and the t=0 its elapsed modes count
 * from (the earliest instant the workspace has loaded).
 *
 * A snapshot of the live workspace, re-supplied by the caller as it changes —
 * so {@link TimestampFormat.FollowVisualizer} inherits every change, including
 * a mode switch made while the reader is looking at the transcript.
 */
export interface VisualizerClock {
  readonly mode: TimeDisplayMode;
  readonly earliestNs?: bigint;
}

/**
 * Everything a format needs beyond the timestamp itself. Passed to every spec
 * so the table stays a plain lookup; formats that don't care simply ignore it.
 */
export interface TimestampRenderOptions {
  /** Digits kept after the decimal point by the fixed-point seconds formats. */
  readonly fractionDigits: FractionDigits;
  /**
   * Epoch-ns instant {@link TimestampFormat.Relative} renders as t=0. Absent
   * when the user has not chosen one — see {@link resolveDisplayFormat}, which
   * keeps Relative off the screen until they do.
   */
  readonly anchorNs?: bigint;
  /**
   * The open visualizer's clock, which
   * {@link TimestampFormat.FollowVisualizer} renders through. Absent when no
   * visualizer is open — see {@link resolveDisplayFormat}, which keeps Follow
   * off the screen until one is.
   */
  readonly visualizerClock?: VisualizerClock;
}

/** What to render with when no user preferences are in reach: no anchor. */
export const DEFAULT_RENDER_OPTIONS: TimestampRenderOptions = {
  fractionDigits: DEFAULT_FRACTION_DIGITS,
};

export interface TimestampFormatSpec {
  readonly label: string;
  readonly description: string;
  readonly format: (ns: bigint, options: TimestampRenderOptions) => string;
}

/**
 * Single source of truth for every per-format fact — label, description, and
 * the formatter itself. Adding a new `TimestampFormat` enum member without
 * extending this table is a TS error (`Record<TimestampFormat, ...>`
 * exhaustiveness). Declaration order also fixes the order
 * {@link TIMESTAMP_FORMATS} exposes — i.e. the order the radio group iterates.
 */
export const TIMESTAMP_FORMAT_SPECS: Readonly<
  Record<TimestampFormat, TimestampFormatSpec>
> = {
  [TimestampFormat.FollowVisualizer]: {
    label: "Follow the visualizer",
    description:
      "Matches the visualizer's time display, including its t=0 for elapsed modes.",
    // Rendered by the visualizer's own formatter rather than a copy of it, so
    // the transcript and the workspace's bottom-left readout cannot drift
    // apart in mode or t=0. The decimal-places setting still applies: the
    // visualizer has a whole panel to spend on nine digits, a sentence of
    // prose does not, and a reader who asked for three everywhere else meant
    // here too. Only the width narrows — the instant is the same one.
    //
    // Reaching this without a clock means a caller skipped
    // resolveDisplayFormat; plain absolute seconds beat rendering against a
    // mode and a t=0 that are not on screen anywhere.
    format: (ns, { fractionDigits, visualizerClock }) =>
      visualizerClock === undefined
        ? `${nanoSecToSecStr(ns, fractionDigits)} s`
        : formatTimeByMode(
            ns,
            visualizerClock.mode,
            visualizerClock.earliestNs,
            true,
            fractionDigits,
          ),
  },
  [TimestampFormat.Nanoseconds]: {
    label: "Nanoseconds (ns)",
    description: "Raw epoch nanoseconds (19-digit integer).",
    format: (ns) => `${ns.toString()} ns`,
  },
  [TimestampFormat.Microseconds]: {
    label: "Microseconds (μs)",
    description: "Epoch microseconds; sub-μs remainder dropped.",
    format: (ns) => `${(ns / 1_000n).toString()} μs`,
  },
  [TimestampFormat.Milliseconds]: {
    label: "Milliseconds (ms)",
    description: "Epoch milliseconds; sub-ms remainder dropped.",
    format: (ns) => `${(ns / NS_PER_MS_BIGINT).toString()} ms`,
  },
  [TimestampFormat.SecondsNanos]: {
    // Named for what it shows, not for the nine digits it used to be pinned
    // to: the decimal width is the reader's to choose, and "Seconds.nanos"
    // read as a promise of nanoseconds. The enum value stays `seconds-nanos` —
    // that is what localStorage holds and what the AI emits in a
    // roboto://ui/time-format link.
    label: "Seconds",
    description: "Epoch seconds, at the chosen decimal precision.",
    format: (ns, { fractionDigits }) =>
      `${nanoSecToSecStr(ns, fractionDigits)} s`,
  },
  [TimestampFormat.Utc]: {
    label: "UTC (ISO 8601)",
    description: "ISO 8601 UTC, at the chosen decimal precision.",
    // ISO 8601 and RFC 3339 both allow any number of fractional-second digits,
    // so the reader's width is expressible here without leaving the standard —
    // at zero the decimal separator goes too.
    format: (ns, { fractionDigits }) =>
      nanoSecToUTCTimestamp(ns, fractionDigits),
  },
  [TimestampFormat.LocalTime]: {
    label: "Local time",
    description:
      "Browser-local date & time in your chosen date/time format, at the chosen decimal precision.",
    format: (ns, { fractionDigits }) =>
      formatDateTimeNsWithOptions(ns, { fractionDigits, timeZone: true }),
  },
  [TimestampFormat.Relative]: {
    label: "Relative",
    description:
      "Seconds elapsed from the thread's t=0 anchor; negative before it.",
    // The sign comes off the rendered string, not the raw difference, so that
    // an offset too small to survive the chosen precision still reads as
    // "+0.000 s" rather than an unsigned "0.000 s" mistakable for an absolute
    // value. Reaching this without an anchor means a caller skipped
    // resolveDisplayFormat; an unsigned epoch beats a bogus "+1771481251 s".
    format: (ns, { fractionDigits, anchorNs }) => {
      if (anchorNs === undefined) {
        return `${nanoSecToSecStr(ns, fractionDigits)} s`;
      }
      const elapsed = nanoSecToSecStr(ns - anchorNs, fractionDigits);
      return `${elapsed.startsWith("-") ? "" : "+"}${elapsed} s`;
    },
  },
};

// --- Derived exports below this line. Edit TIMESTAMP_FORMAT_SPECS, not these. ---

export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat =
  TimestampFormat.SecondsNanos;

export const TIMESTAMP_FORMATS: readonly TimestampFormat[] = Object.keys(
  TIMESTAMP_FORMAT_SPECS,
) as TimestampFormat[];

/**
 * Render a wall-clock ns bigint in the requested format. Every numeric
 * format carries its own unit suffix (`ns`, `μs`, `ms`, `s`). ISO 8601 and
 * local-time strings are self-describing and carry no suffix.
 */
export function formatTimestamp(
  ns: bigint,
  format: TimestampFormat,
  options: TimestampRenderOptions = DEFAULT_RENDER_OPTIONS,
): string {
  return TIMESTAMP_FORMAT_SPECS[format].format(ns, options);
}

/**
 * The format to actually render with. The two context-dependent formats are
 * selectable before the thing they depend on exists — Relative without an
 * anchor would show every timestamp against an origin the user never chose,
 * and Follow with no visualizer open has no clock to mirror — so each falls
 * back to whichever absolute format the reader last used until its context
 * arrives, and switches over silently once it does.
 */
export function resolveDisplayFormat(
  format: TimestampFormat,
  anchorNs: bigint | undefined,
  absoluteFallback: TimestampFormat,
  visualizerClock: VisualizerClock | undefined,
): TimestampFormat {
  if (format === TimestampFormat.Relative && anchorNs === undefined) {
    return absoluteFallback;
  }
  if (
    format === TimestampFormat.FollowVisualizer &&
    visualizerClock === undefined
  ) {
    return absoluteFallback;
  }
  return format;
}

/**
 * Whether `format` can serve as the absolute fallback an unresolvable Relative
 * or Follow degrades to. Neither of those two can: Relative would render
 * against an origin nobody chose, and Follow would recurse into the very
 * absence that sent it here.
 */
export function isAbsoluteTimestampFormat(format: TimestampFormat): boolean {
  return (
    format !== TimestampFormat.Relative &&
    format !== TimestampFormat.FollowVisualizer
  );
}

export function isTimestampFormat(value: unknown): value is TimestampFormat {
  return (
    typeof value === "string" &&
    (TIMESTAMP_FORMATS as readonly string[]).includes(value)
  );
}
